import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { patch, updateItem } from '@ngxs/store/operators';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { IFile, IFileElement } from '../../interfaces/file-element.interface';
import { CloudService } from '../../services/cloud.service';
import {
    CreateFile,
    CreateFolder,
    CreateRootFolder,
    DeleteFile,
    DeleteFolder,
    GetFolderById,
    GetRootFolder,
    GoBackToParentFolder,
    NavigateToParent,
    ResetCloudData,
    SearchFiles,
    UpdateFile,
    UpdateFolder,
} from '../actions/cloud.actions';

export interface CloudStateModel {
    root: IFileElement[];
    parentFolders: IFileElement[];
    currentFolder: IFileElement;
    searchResults: IFileElement;
}

@State<CloudStateModel>({
    name: 'files',
    defaults: {
        root: [],
        parentFolders: [],
        currentFolder: null,
        searchResults: null,
    },
})
@Injectable()
export class CloudState {
    constructor(private cloudService: CloudService) {}

    @Selector()
    public static rootFolder(state: CloudStateModel): IFileElement[] {
        return state.root;
    }

    @Selector()
    public static parentFolders(state: CloudStateModel): IFileElement[] {
        return state.parentFolders;
    }

    @Selector()
    public static currentFiles(state: CloudStateModel): IFileElement {
        return state.currentFolder;
    }

    @Selector()
    public static searchResults(state: CloudStateModel): IFileElement {
        return state.searchResults;
    }

    @Action(GetRootFolder)
    public getRootFolder(ctx: StateContext<CloudStateModel>): Observable<IFileElement[]> {
        return this.cloudService.getRootFolder().pipe(
            tap((result: IFileElement[]) => {
                if (result) {
                    ctx.patchState({
                        root: result,
                    });
                }
            })
        );
    }

    @Action(GetFolderById)
    public getFolderById(ctx: StateContext<CloudStateModel>, action: GetFolderById): Observable<IFileElement> {
        return this.cloudService.getFolderById(action.parentFolder.id).pipe(
            tap((result: IFileElement) => {
                if (result) {
                    const currentFolder: IFileElement = ctx.getState().currentFolder;
                    if (currentFolder) {
                        ctx.patchState({
                            parentFolders: [...ctx.getState().parentFolders, currentFolder],
                        });
                    }
                    ctx.patchState({
                        currentFolder: result,
                    });
                }
            })
        );
    }

    @Action(CreateRootFolder)
    public createRootFolder(ctx: StateContext<CloudStateModel>, action: CreateRootFolder): Observable<IFileElement> {
        const state: CloudStateModel = ctx.getState();
        return this.cloudService.createRootFolder(action.newFolder).pipe(
            tap((result: IFileElement) => {
                if (result) {
                    ctx.patchState({
                        root: [...state.root, result],
                    });
                }
            })
        );
    }

    @Action(CreateFolder)
    public createFolder(ctx: StateContext<CloudStateModel>, action: CreateFolder): Observable<IFileElement> {
        return this.cloudService.createFolder(action.parentFolder.id, action.newFolder).pipe(
            tap((result: IFileElement) => {
                if (result) {
                    ctx.patchState({
                        currentFolder: result,
                    });
                }
            })
        );
    }

    @Action(UpdateFolder)
    public updateFolder(ctx: StateContext<CloudStateModel>, action: UpdateFolder): Observable<IFileElement> {
        const currentFolder: IFileElement = ctx.getState().currentFolder;
        return this.cloudService.updateFolder(action.folderId, action.newFolder).pipe(
            tap((result: IFileElement) => {
                if (result) {
                    if (currentFolder) {
                        ctx.setState(
                            patch({
                                currentFolder: patch({
                                    ...currentFolder,
                                    folders: updateItem<IFileElement>((item: IFileElement) => item.id === action.folderId, result),
                                }),
                            })
                        );
                    } else {
                        ctx.setState(
                            patch({
                                root: updateItem<IFileElement>((item: IFileElement) => item.id === action.folderId, result),
                            })
                        );
                    }
                }
            })
        );
    }

    @Action(DeleteFolder)
    public deleteFolder(ctx: StateContext<CloudStateModel>, action: DeleteFolder): Observable<void> {
        const currentFolder: IFileElement = ctx.getState().currentFolder;
        return this.cloudService.deleteFolder(action.folderId).pipe(
            tap(() => {
                if (currentFolder) {
                    ctx.patchState({
                        currentFolder: {
                            ...currentFolder,
                            folders: currentFolder.folders.filter((folder: IFileElement) => folder.id !== action.folderId),
                        },
                    });
                } else {
                    const rootFolder: IFileElement[] = ctx.getState().root;
                    ctx.patchState({
                        root: rootFolder.filter((folder: IFileElement) => folder.id !== action.folderId),
                    });
                }
            })
        );
    }

    @Action(CreateFile)
    public createFile(ctx: StateContext<CloudStateModel>, action: CreateFile): Observable<IFile> {
        const state: CloudStateModel = ctx.getState();
        return this.cloudService.createFile(action.parentFolderId, action.newFile).pipe(
            tap((result: IFile) => {
                if (result) {
                    ctx.patchState({
                        currentFolder: {
                            ...state.currentFolder,
                            items: [...state.currentFolder.items, result],
                        },
                    });
                }
            })
        );
    }

    @Action(UpdateFile)
    public updateFile(ctx: StateContext<CloudStateModel>, action: UpdateFile): Observable<IFile> {
        const currentFolder: IFileElement = ctx.getState().currentFolder;
        return this.cloudService.updateFile(action.folderId, action.fileId, action.file).pipe(
            tap((result: IFile) => {
                if (result) {
                    if (!action.isSearch) {
                        ctx.setState(
                            patch({
                                currentFolder: patch({
                                    ...currentFolder,
                                    items: updateItem<IFile>((item: IFile) => item.id === action.fileId, result),
                                }),
                            })
                        );
                    } else {
                        ctx.setState(
                            patch({
                                searchResults: patch({
                                    items: updateItem<IFile>((item: IFile) => item.id === action.fileId, result),
                                }),
                            })
                        );
                    }
                }
            })
        );
    }

    @Action(DeleteFile)
    public deleteFile(ctx: StateContext<CloudStateModel>, action: DeleteFile): Observable<void> {
        const currentFolder: IFileElement = ctx.getState().currentFolder;
        const searchResult: IFileElement = ctx.getState().searchResults;
        return this.cloudService.deleteFile(action.parentFolderId, action.fileId).pipe(
            tap(() => {
                if (!action.isSearch) {
                    ctx.patchState({
                        currentFolder: {
                            ...currentFolder,
                            items: currentFolder.items.filter((file: IFile) => file.id !== action.fileId),
                        },
                    });
                } else {
                    ctx.patchState({
                        searchResults: {
                            ...searchResult,
                            items: searchResult.items.filter((file: IFile) => file.id !== action.fileId),
                        },
                    });
                }
            })
        );
    }

    @Action(SearchFiles)
    public searchFiles(ctx: StateContext<CloudStateModel>, action: SearchFiles): Observable<IFileElement> {
        return this.cloudService.searchFiles(action.searchValue).pipe(
            tap((result: IFileElement) => {
                if (result) {
                    ctx.patchState({
                        searchResults: result,
                    });
                }
            })
        );
    }

    @Action(GoBackToParentFolder)
    public goBackToParentFolder(ctx: StateContext<CloudStateModel>): Observable<IFileElement[] | void> {
        const state: CloudStateModel = ctx.getState();
        if (state.parentFolders.length) {
            const parentFolder: IFileElement = state.parentFolders[state.parentFolders.length - 1];
            ctx.patchState({
                currentFolder: parentFolder,
                parentFolders: state.parentFolders.filter((folder: IFileElement) => folder.id !== parentFolder.id),
            });
        } else {
            ctx.patchState({
                currentFolder: null,
            });
        }
        return null;
    }

    @Action(NavigateToParent)
    public navigateToParent(ctx: StateContext<CloudStateModel>, action: NavigateToParent): Observable<void> {
        const state: CloudStateModel = ctx.getState();
        if (action.parentFolderId) {
            const parentFolderIndex: number = state.parentFolders.findIndex((folder: IFileElement) => folder.id === action.parentFolderId);
            const parentFolder: IFileElement = state.parentFolders[parentFolderIndex];
            ctx.patchState({
                currentFolder: parentFolder,
                parentFolders: state.parentFolders.slice(0, parentFolderIndex),
            });
        } else {
            ctx.patchState({
                currentFolder: null,
                parentFolders: [],
            });
        }
        return null;
    }

    @Action(ResetCloudData)
    public resetCloudData(ctx: StateContext<CloudStateModel>): void {
        ctx.patchState({
            root: [],
            parentFolders: [],
            currentFolder: null,
        });
    }
}
